import './style.css';
import { DateComponent, LoadingComponent } from '../../controls';
import { usePackReducer } from './usePackReducer';
import { Pack, PackStatus } from '../../types/pack';
import {
  formatPackSizeDiffKb,
  formatPackSizeKb,
  formatRelativeDays,
  isPackDeletable,
} from './packFormat';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { ButtonGroup } from 'primereact/buttongroup';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import React from 'react';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const NO_VERSION_LIMIT = '__no_limit__';

const compareVersionsDesc = (a: string, b: string): number => {
  const parse = (v: string) => v.split('.').map((n) => parseInt(n, 10) || 0);
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pb[i] ?? 0) - (pa[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return b.localeCompare(a);
};

const PackPage = () => {
  const { data, open, release, validate, updateVersionLimit, remove } = usePackReducer();
  const isOpenAllowed = data.Records.findIndex((r) => r.status === PackStatus.Pending) === -1;
  const isReleaseAllowed = data.Records.findIndex((r) => r.status === PackStatus.Pending) !== -1;

  const packsByVersion = React.useMemo(() => {
    const map = new Map<number, Pack>();
    data.Records.forEach((pack) => map.set(pack.version, pack));
    return map;
  }, [data.Records]);

  const versionLimitOptions = React.useMemo(() => {
    const versionSet = new Set<string>(data.ClientVersions);
    data.Records.forEach((r) => {
      if (r.versionLimit) versionSet.add(r.versionLimit);
    });
    return [NO_VERSION_LIMIT, ...[...versionSet].sort(compareVersionsDesc)];
  }, [data.ClientVersions, data.Records]);

  if (data.Loading && data.Records.length === 0) {
    return <LoadingComponent />;
  }

  const statusBodyTemplate = (status: PackStatus) => {
    switch (status) {
      case PackStatus.Deprecated:
        return <Tag value="deprecated" severity="warning" />;
      case PackStatus.Pending:
        return <Tag value="pending" severity="secondary" />;
      default:
        return <Tag value="released" severity="success" />;
    }
  };

  const versionBodyTemplate = (pack: Pack) => (
    <span className="pack-version" title={pack.checksum ?? undefined}>
      {pack.version}
    </span>
  );

  const sizeBodyTemplate = (pack: Pack) => {
    const previousPack = packsByVersion.get(pack.version - 1);
    const diff = previousPack
      ? formatPackSizeDiffKb(pack.sizeBytes, previousPack.sizeBytes)
      : null;

    return (
      <span>
        {formatPackSizeKb(pack.sizeBytes)}
        {diff ? ` ${diff}` : ''}
      </span>
    );
  };

  const lastDownloadBodyTemplate = (pack: Pack) => {
    const isStale = Date.now() - pack.lastDownloadDate.getTime() > WEEK_MS;
    return (
      <span className={isStale ? 'pack-last-download--stale' : undefined}>
        {formatRelativeDays(pack.lastDownloadDate)}
      </span>
    );
  };

  const versionLimitBodyTemplate = (pack: Pack) => (
    <Dropdown
      value={pack.versionLimit ?? NO_VERSION_LIMIT}
      options={versionLimitOptions}
      onChange={(e) =>
        updateVersionLimit(pack, e.value === NO_VERSION_LIMIT ? null : e.value)
      }
      itemTemplate={(version) => (version === NO_VERSION_LIMIT ? 'No limit' : version)}
      valueTemplate={(version) =>
        version === NO_VERSION_LIMIT || !version ? 'No limit' : version
      }
      appendTo={document.body}
      className="pack-version-limit-dropdown"
    />
  );

  const handleDelete = (pack: Pack) => {
    confirmDialog({
      message: `Are you sure you want to delete pack version ${pack.version}?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => remove(pack),
    });
  };

  const actionsTemplate = (pack: Pack) => {
    return (
      <>
        <Button
          icon={`pi pi-search`}
          disabled={pack.status !== PackStatus.Pending}
          onClick={() => validate(pack)}
          rounded
          text
          size="small"
        />
        {isPackDeletable(pack.status, pack.lastDownloadDate) && (
          <Button
            icon="pi pi-trash"
            onClick={() => handleDelete(pack)}
            rounded
            text
            severity="danger"
            size="small"
          />
        )}
      </>
    );
  };

  return (
    <div className="page-pack p-3">
      <ConfirmDialog />
      <Toolbar
        end={
          <ButtonGroup>
            {isOpenAllowed && <Button onClick={open} label="Open version" size="small" />}
            {isReleaseAllowed && <Button onClick={release} label="Release version" size="small" />}
          </ButtonGroup>
        }
      />

      <div className="mt-3">
        <DataTable value={data.Records} responsiveLayout="scroll" scrollable scrollHeight="flex">
          <Column header="Version" body={(item: Pack) => versionBodyTemplate(item)}></Column>
          <Column header="Status" body={(item: Pack) => statusBodyTemplate(item.status)}></Column>
          <Column header="Size" body={(item: Pack) => sizeBodyTemplate(item)}></Column>
          <Column
            header="Version limit"
            body={(item: Pack) => versionLimitBodyTemplate(item)}
          ></Column>
          <Column
            header="Created"
            body={(item: Pack) => <DateComponent date={item.createdAt} />}
          ></Column>
          <Column
            header="Updated"
            body={(item: Pack) => <DateComponent date={item.updatedAt} />}
          ></Column>
          <Column
            header="Last download"
            body={(item: Pack) => lastDownloadBodyTemplate(item)}
          ></Column>
          <Column header="Actions" body={(item: Pack) => actionsTemplate(item)}></Column>
        </DataTable>
      </div>
    </div>
  );
};

export default PackPage;
